import { AfterViewInit, Component, ElementRef, HostBinding, Input, Renderer2, ViewChild } from '@angular/core';
import { CardItemCover, CardItemList, CardItemProgress, CardItemLink } from './card-item.interface';
import { DominantHexColors } from '../../../components/card-item/models/dominant-colors.model';
import { UrlTree } from '@angular/router';

@Component({
  selector: 'opac-card-item',
  templateUrl: './card-item.component.html',
  styleUrls: ['./card-item.component.scss']
})
export class CardItemComponent implements AfterViewInit {
  private _author?: string;

  authorQuery?: { query: string };

  /**
   * Data given to record-cover component
   */
  @Input() cover: CardItemCover;
  /**
   * Data given to progress-bar component.
   * Won't render progress if `undefined`.
   */
  @Input() progress?: CardItemProgress;
  /**
   * Record title
   */
  @Input() title: string;
  /**
   * Where router should navigate when clicked. Data passed to routerLink directive.
   * Won't render hover effect and prevent navigation if `undefined`.
   */
  @Input() link?: CardItemLink;
  /**
   * Data to show instead of author.
   * Only shows if it isn't `undefined`.
   */
  @Input() list?: CardItemList;
  /**
   * Issue date to show as tag close to title if set.
   */
  @Input() issueDate?: string;
  /**
   * Dominant colors to present as background if set.
   * Default colors if `undefined`.
   */
  @Input() dominantColors?: DominantHexColors;

  /**
   * Determines author and author link (based on author name).
   * Only shows this information if data is not `undefined`.
   */
  @Input()
  set author(value: string) {
    this._author = value;
    this.authorQuery = { query: `author:"${this._author}"` };
  }
  get author(): string | undefined {
    return this._author;
  }

  /**
   * Sets custom class when link is undefined to prevent hover effect.
   */
  @HostBinding('class.not-linked') get notLinkedClass() {
    return this.link === undefined;
  }
  /**
   * Sets gradient class when dominantColors is active to change some text colors.
   */
  @HostBinding('class.gradient') get gradientClass() {
    return this.dominantColors?.showGradient;
  }

  @ViewChild('cardContainer') cardContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private readonly renderer: Renderer2
  ) { }

  ngAfterViewInit() {
    this.addGradientToBackground();
  }

  private addGradientToBackground() {
    if (!this.dominantColors?.showGradient) {
      return;
    }

    const dominantColorRgb = `rgba(${this.dominantColors.dominant.join(',')})`;
    const secondaryColorRgb = `rgba(${this.dominantColors.secondary.join(',')})`;

    this.renderer.setStyle(
      this.cardContainer.nativeElement,
      'background-image',
      'linear-gradient(to bottom, hsla(var(--primary-background),.9), hsla(var(--primary-background),.7) 70%, hsla(var(--primary-background),.4)),' +
      `linear-gradient(to right, ${dominantColorRgb}, ${dominantColorRgb} 33%, ${secondaryColorRgb})`
      );
  }
}
